import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import CopilotBlockRenderer from "./CopilotBlocks";
import CopilotMarkdown from "./CopilotMarkdown";
import { copyCopilotText } from "./CopilotComposer";
import { useCopilotShell } from "./CopilotShellContext";
import {
  filterDuplicateActions,
  getAssistantDisplayContent,
  groupBlocksForDisplay,
  formatWorkflowStepHint,
  formatWorkflowStepLabel,
  pick,
  WORKFLOW_LABELS,
} from "./copilotUtils";
import { usePostDataMutation } from "../../app/services/commonApi";
import Toast from "@moltech/molui/toast";

const CopilotTurnFeedback = ({ message }) => {
  const { threads } = useCopilotShell();
  const [postData] = usePostDataMutation();
  const [rating, setRating] = useState(null);
  const [commentOpen, setCommentOpen] = useState(false);
  const [comment, setComment] = useState("");
  const logId = pick(message, "aiExecutionLogId", "AIExecutionLogId");
  const conversationId = threads?.activeThread?.conversationId;

  const submit = async (nextRating, nextComment) => {
    try {
      await postData({
        url: "/AIExecution/SubmitFeedback",
        data: {
          aiExecutionLogId: logId ? Number(logId) : null,
          conversationId: conversationId || null,
          rating: nextRating,
          comment: nextComment || null,
        },
      }).unwrap();
      setRating(nextRating);
      Toast.success("Thanks for the feedback.");
    } catch (err) {
      Toast.error(err?.data?.message || err?.message || "Could not save feedback.");
    }
  };

  return (
    <div className="copilot-turn-feedback">
      <button
        type="button"
        className={`copilot-turn-feedback__btn${rating === 1 ? " is-active" : ""}`}
        title="Helpful"
        onClick={() => submit(1)}
      >
        <i className="pi pi-thumbs-up" />
      </button>
      <button
        type="button"
        className={`copilot-turn-feedback__btn${rating === -1 ? " is-active" : ""}`}
        title="Not helpful"
        onClick={() => {
          setCommentOpen(true);
          submit(-1);
        }}
      >
        <i className="pi pi-thumbs-down" />
      </button>
      {commentOpen ? (
        <form
          className="copilot-turn-feedback__comment"
          onSubmit={(e) => {
            e.preventDefault();
            submit(-1, comment);
            setCommentOpen(false);
          }}
        >
          <input
            className="p-inputtext p-component p-inputtext-sm"
            value={comment}
            maxLength={500}
            placeholder="Optional comment"
            onChange={(e) => setComment(e.target.value)}
          />
          <Button type="submit" label="Send" className="p-button-sm p-button-text" />
        </form>
      ) : null}
    </div>
  );
};

CopilotTurnFeedback.propTypes = {
  message: PropTypes.object.isRequired,
};

const LOADING_STATUSES = [
  "Reviewing your request",
  "Processing",
  "Getting response",
];

const CopilotBlockGroup = ({
  group,
  onNavigate,
  onAction,
  workflow,
  formDisabled,
  onFormSave,
  onFormCancel,
  onFormRefresh,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isGroupedList =
    group.kind === "jobopeningcard" ||
    group.kind === "candidatecard" ||
    group.kind === "interviewercard" ||
    group.kind === "slotcard";
  const count = group.blocks.length;
  const customTitle = pick(group.blocks[0], "title", "Title");
  const title =
    customTitle ||
    (group.kind === "jobopeningcard"
      ? `Published openings`
      : group.kind === "candidatecard"
        ? `Candidates`
        : group.kind === "interviewercard"
          ? `Interviewers`
          : group.kind === "slotcard"
            ? `Slots`
            : null);

  if (!isGroupedList) {
    return (
      <CopilotBlockRenderer
        block={group.blocks[0]}
        onNavigate={onNavigate}
        onAction={onAction}
        workflow={workflow}
        formDisabled={formDisabled}
        onFormSave={onFormSave}
        onFormCancel={onFormCancel}
        onFormRefresh={onFormRefresh}
      />
    );
  }

  const preview = 10;
  const showAll = expanded || count <= preview;
  const visible = showAll ? group.blocks : group.blocks.slice(0, preview);

  return (
    <div className="copilot-block-group">
      <div className="copilot-block-group__header">
        <span className="copilot-block-group__title">{title}</span>
        <span className="copilot-block-group__count">{count}</span>
      </div>
      <div className="copilot-block-group__list">
        {visible.map((block, i) => (
          <CopilotBlockRenderer
            key={`${group.key}-${i}`}
            block={block}
            onNavigate={onNavigate}
            onAction={onAction}
            compact
          />
        ))}
      </div>
      {count > preview ? (
        <button
          type="button"
          className="copilot-block-group__more"
          onClick={() => setExpanded((v) => !v)}
        >
          {showAll ? "Show less" : `Show ${count - preview} more`}
        </button>
      ) : null}
    </div>
  );
};

CopilotBlockGroup.propTypes = {
  group: PropTypes.object.isRequired,
  onNavigate: PropTypes.func,
  onAction: PropTypes.func,
  workflow: PropTypes.object,
  formDisabled: PropTypes.bool,
  onFormSave: PropTypes.func,
  onFormCancel: PropTypes.func,
  onFormRefresh: PropTypes.func,
};

const CopilotMessageList = ({
  messages,
  isLoading,
  activeWorkflow,
  onAction,
  onNavigate,
  onCancelWorkflow,
  onDraftPrompt,
  onFormSave,
  onFormRefresh,
  onSendEmailDraft,
  isSendingEmailDraft,
}) => {
  const { debugMode } = useCopilotShell();
  const bottomRef = useRef(null);
  const [statusPhase, setStatusPhase] = useState(0);
  const [emailReviewOpen, setEmailReviewOpen] = useState(false);
  const [emailReviewDraft, setEmailReviewDraft] = useState({ subject: "", recipients: [] });
  const [emailPreviewIndex, setEmailPreviewIndex] = useState(0);
  const lastMessage = messages[messages.length - 1];
  const lastMessageFingerprint = lastMessage
    ? `${lastMessage.role}:${String(lastMessage.content || "").length}:${String(lastMessage.errorText || "").length}:${Array.isArray(lastMessage.blocks) ? lastMessage.blocks.length : 0}`
    : "";

  useEffect(() => {
    if (!isLoading) {
      setStatusPhase(0);
      return undefined;
    }

    setStatusPhase(0);
    const processingTimer = window.setTimeout(() => setStatusPhase(1), 1200);
    const gettingResponseTimer = window.setTimeout(() => setStatusPhase(2), 2800);

    return () => {
      window.clearTimeout(processingTimer);
      window.clearTimeout(gettingResponseTimer);
    };
  }, [isLoading]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, isLoading, lastMessageFingerprint]);

  const lastAssistantIndex = [...messages]
    .map((m, i) => ({ m, i }))
    .reverse()
    .find((x) => x.m.role === "assistant")?.i;
  const lastAssistant =
    lastAssistantIndex != null ? messages[lastAssistantIndex] : null;
  const workflowType =
    pick(activeWorkflow, "type", "Type") || pick(lastAssistant?.workflow, "type", "Type");
  const workflowStep =
    pick(activeWorkflow, "step", "Step") || pick(lastAssistant?.workflow, "step", "Step");

  return (
    <div className="copilot-chat">
      {activeWorkflow ? (
        <div className="copilot-workflow-bar" role="status">
          <div className="copilot-workflow-bar__main">
            <span className="copilot-workflow-bar__icon" aria-hidden="true">
              <i className="pi pi-sync" />
            </span>
            <div className="copilot-workflow-bar__text">
              <span className="copilot-workflow-bar__title">
                {WORKFLOW_LABELS[workflowType] || workflowType || "Guided action"}
              </span>
              {workflowStep ? (
                <>
                  <span className="copilot-workflow-bar__step">
                    Current step:{" "}
                    <strong>{formatWorkflowStepLabel(workflowStep)}</strong>
                    <span className="copilot-workflow-bar__step-tag">status</span>
                  </span>
                  <span className="copilot-workflow-bar__hint">
                    {formatWorkflowStepHint(workflowStep)}
                  </span>
                </>
              ) : (
                <span className="copilot-workflow-bar__step">
                  In progress — confirm before any change is saved
                </span>
              )}
            </div>
          </div>
          <Button
            type="button"
            label="Cancel"
            icon="pi pi-times"
            className="p-button-outlined p-button-sm copilot-workflow-bar__cancel"
            disabled={isLoading}
            onClick={onCancelWorkflow}
            aria-label="Cancel guided action"
          />
        </div>
      ) : null}

      <div className="copilot-chat__messages">
        {messages.map((m, idx) => {
          const isAssistant = m.role === "assistant";
          const displayContent = isAssistant
            ? getAssistantDisplayContent(m)
            : m.content;
          const blockGroups = isAssistant ? groupBlocksForDisplay(m.blocks) : [];
          const actions = isAssistant
            ? filterDuplicateActions(m.suggestedActions, m.blocks)
            : m.suggestedActions;

          return (
            <article
              key={`${m.role}-${idx}`}
              className={`copilot-chat__bubble copilot-chat__bubble--${m.role}${m.isError ? " copilot-chat__bubble--error" : ""}`}
            >
              <div className="copilot-chat__bubble-head">
                {isAssistant ? (
                  <span className="copilot-chat__avatar copilot-chat__avatar--ai">
                    <i className="pi pi-sparkles" />
                  </span>
                ) : (
                  <span className="copilot-chat__avatar copilot-chat__avatar--user">
                    <i className="pi pi-user" />
                  </span>
                )}
                <span className="copilot-chat__sender">
                  {isAssistant ? "MolAssist" : "You"}
                </span>
              </div>

              {displayContent ? (
                <div className="copilot-chat__content">
                  {isAssistant ? (
                    <CopilotMarkdown
                      text={displayContent}
                      onBulletClick={onDraftPrompt}
                    />
                  ) : (
                    displayContent
                  )}
                </div>
              ) : null}

              {debugMode && isAssistant && m.errorText ? (
                <pre className="copilot-chat__error-detail">{m.errorText}</pre>
              ) : null}

              {blockGroups.length ? (
                <div className="copilot-chat__blocks">
                  {blockGroups.map((group) => (
                    <CopilotBlockGroup
                      key={group.key}
                      group={group}
                      onNavigate={onNavigate}
                      onAction={onAction}
                      workflow={activeWorkflow || m.workflow}
                      formDisabled={isLoading || idx !== lastAssistantIndex}
                      onFormSave={onFormSave}
                      onFormCancel={onCancelWorkflow}
                      onFormRefresh={onFormRefresh}
                    />
                  ))}
                </div>
              ) : null}

              {isAssistant &&
              pick(m, "pendingConfirmation", "PendingConfirmation") &&
              !(m.blocks || []).some(
                (b) => String(pick(b, "type", "Type") || "").toLowerCase() === "confirmation"
              ) ? (
                <div className="copilot-chat__blocks">
                  <CopilotBlockRenderer
                    block={{
                      type: "confirmation",
                      title: "Action preview",
                      markdown: pick(m, "pendingConfirmation", "PendingConfirmation"),
                    }}
                    onAction={onAction}
                  />
                </div>
              ) : null}

              {isAssistant && !m.isError ? <CopilotTurnFeedback message={m} /> : null}

              {m.draftEmailText ? (
                <div className="copilot-email-draft">
                  <div className="copilot-email-draft__header">
                    <strong>Email draft (copy only)</strong>
                    <Button
                      type="button"
                      label="Copy"
                      icon="pi pi-copy"
                      className="p-button-sm p-button-text"
                      onClick={() => copyCopilotText(m.draftEmailText)}
                    />
                    {Array.isArray(m.draftEmailRecipients) &&
                    m.draftEmailRecipients.length > 0 ? (
                      <Button
                        type="button"
                        label="Review & Send"
                        icon="pi pi-send"
                        className="p-button-sm p-button-text"
                        onClick={() => {
                          setEmailReviewDraft({
                            subject: m.draftEmailSubject || "",
                            recipients: m.draftEmailRecipients || [],
                          });
                          setEmailPreviewIndex(0);
                          setEmailReviewOpen(true);
                        }}
                      />
                    ) : null}
                  </div>
                  <pre className="copilot-email-draft__body">{m.draftEmailText}</pre>
                </div>
              ) : null}

              {actions?.length ? (
                <div className="copilot-chat__actions">
                  {actions.slice(0, 4).map((a, i) => (
                    <button
                      key={`${pick(a, "label", "Label")}-${i}`}
                      type="button"
                      className="copilot-action-chip"
                      onClick={() => onAction(a)}
                    >
                      {pick(a, "label", "Label") || "Open"}
                    </button>
                  ))}
                </div>
              ) : null}
            </article>
          );
        })}

        {isLoading ? (
          <div className="copilot-chat__typing">
            <span className="copilot-chat__avatar copilot-chat__avatar--ai">
              <i className="pi pi-sparkles" />
            </span>
            <span className="copilot-chat__typing-dots">
              <span />
              <span />
              <span />
            </span>
            <span className="copilot-chat__typing-status" aria-live="polite">
              {LOADING_STATUSES[statusPhase]}
            </span>
          </div>
        ) : null}
        <div ref={bottomRef} className="copilot-chat__scroll-anchor" aria-hidden="true" />
        <Dialog
          visible={emailReviewOpen}
          onHide={() => setEmailReviewOpen(false)}
          header="Review & Send email shortlist"
          modal
          style={{ width: "900px", maxWidth: "95vw" }}
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              <Button
                type="button"
                label="Cancel"
                className="p-button-text"
                onClick={() => setEmailReviewOpen(false)}
                disabled={isSendingEmailDraft}
              />
              <Button
                type="button"
                label={isSendingEmailDraft ? "Sending..." : "Send"}
                icon="pi pi-send"
                disabled={isSendingEmailDraft || !emailReviewDraft.recipients?.length}
                onClick={async () => {
                  await onSendEmailDraft?.(emailReviewDraft);
                  setEmailReviewOpen(false);
                }}
              />
            </div>
          }
        >
          <div className="copilot-email-review">
            <div style={{ marginBottom: "0.75rem" }}>
              <strong>Subject:</strong> <span>{emailReviewDraft.subject}</span>
            </div>

            <div style={{ marginBottom: "0.75rem" }}>
              <strong>Recipients ({emailReviewDraft.recipients.length}):</strong>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "0.5rem" }}>
                {emailReviewDraft.recipients.map((r, idx) => (
                  <button
                    key={String(r.applicantId ?? idx)}
                    type="button"
                    className="copilot-email-review__recipient-chip"
                    onClick={() => setEmailPreviewIndex(idx)}
                    style={{
                      border:
                        idx === emailPreviewIndex ? "1px solid var(--primary-color)" : "1px solid #d0d0d0",
                      padding: "0.25rem 0.5rem",
                      borderRadius: "6px",
                      cursor: "pointer",
                      background: idx === emailPreviewIndex ? "rgba(0,0,0,0.02)" : "transparent",
                    }}
                  >
                    {r.applicantName || r.applicantEmail || `Recipient ${idx + 1}`}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <strong>Preview (recipient {emailPreviewIndex + 1}):</strong>
              <pre style={{ whiteSpace: "pre-wrap" }}>
                {emailReviewDraft.recipients[emailPreviewIndex]?.body || ""}
              </pre>
            </div>
          </div>
        </Dialog>
      </div>
    </div>
  );
};

CopilotMessageList.propTypes = {
  messages: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  activeWorkflow: PropTypes.object,
  onAction: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onCancelWorkflow: PropTypes.func.isRequired,
  onDraftPrompt: PropTypes.func,
  onFormSave: PropTypes.func,
  onFormRefresh: PropTypes.func,
  onSendEmailDraft: PropTypes.func,
  isSendingEmailDraft: PropTypes.bool,
};

export default CopilotMessageList;
